import React, { useEffect } from "react";
import { Grid, Pagination } from "semantic-ui-react";
import useEventDetailsGrid from "@hooks/useEventDetailsGrid";
import EventDetailsStatuses from "./EventDetailsStatuses";
import useComplianceSummaryFilters from "@hooks/useComplianceSummaryFilters";
import EventDetailsForm from "./EventDetailsForm";
import useComplianceView from "@hooks/useComplianceView";
import EventDetailsTable from "./EventDetailsTable";
import ExpandButton from "@buttons/ExpandButton";
import useLogin from "@common/components/hooks/useLogin";
import useCustomerReview from "@common/components/hooks/useCustomerReview";

const EventDetailsTab = () => {
  const { activeRow, onRefresh } = useEventDetailsGrid();
  const { open } = useCustomerReview();
  const {
    section,
    operator,
    filters: { startDate, endDate },
    hideDetails,
    totalRecords,
    isLoading,
    hasSelection,
    companyId,
    subcompanyId,
    siteId,
    setViewData,
  } = useComplianceView();
  const {
    activePage,
    onPageChange,
    periods,
    selectedStatuses,
    selectedPeriod,
    processedQuarters,
    selectedQuarter,
  } = useComplianceSummaryFilters();
  const [{ isInReadMode }] = useLogin();

  useEffect(onRefresh, [
    activePage,
    selectedStatuses,
    isInReadMode,
    hasSelection,
    periods,
    selectedPeriod,
    processedQuarters,
    selectedQuarter,
  ]);

  // Reset active page and name search input when switching sites
  useEffect(() => {
    setViewData({ allNames: "" });
    onRefresh(false, false, "", true);
  }, [siteId]);

  // refresh & keep the active row when the review modal is closed.
  useEffect(() => {
    if (open) {
      return;
    }
    onRefresh(false, true);
  }, [open]);

  // Open date range modal if no date range is selected,
  // forcing the user to select date range before loading any events data
  useEffect(() => {
    if (
      section === "COMPLIANCE" &&
      operator &&
      !startDate &&
      !endDate &&
      (companyId || subcompanyId || siteId)
    ) {
      setViewData({ showDateRangeModal: true });
    }
  }, [section, operator, startDate, endDate, companyId, subcompanyId, siteId]);

  const isDetailsVisible = activeRow && !hideDetails;

  const widescreen = isDetailsVisible ? 10 : 16;
  const largeScreen = isDetailsVisible ? 9 : 16;
  const computer = isDetailsVisible ? 8 : 16;
  const tablet = 16;
  const mobile = 16;

  const recordsPerPage = 25;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const currentItem = (activePage - 1) * 25 + 1;
  const lastItem = Math.min(activePage * recordsPerPage, totalRecords);

  return (
    <div className="flex-stretch gs-flex-container-vertical height-100">
      <Grid className="height-100 gs-flex-container-vertical">
        <Grid.Row>
          <Grid.Column width={12}>
            <EventDetailsStatuses />
          </Grid.Column>
          {activeRow && (
            <Grid.Column width={4}>
              <ExpandButton
                content={hideDetails ? "Show Details" : "Hide Details"}
                compact={false}
                onClick={() => setViewData({ hideDetails: !hideDetails })}
              />
            </Grid.Column>
          )}
        </Grid.Row>
        <Grid.Row className="flex-stretch">
          <Grid.Column
            className="gs-flex-container-vertical"
            widescreen={widescreen}
            largeScreen={largeScreen}
            computer={computer}
            tablet={tablet}
            mobile={mobile}
          >
            <EventDetailsTable />
          </Grid.Column>
          {isDetailsVisible && (
            <Grid.Column
              className="gs-flex-container-vertical"
              widescreen={16 - widescreen}
              largeScreen={16 - largeScreen}
              computer={16 - computer}
              tablet={16}
              mobile={16}
            >
              <EventDetailsForm />
            </Grid.Column>
          )}
        </Grid.Row>
        {totalPages > 0 && (
          <Grid.Row>
            <Grid.Column width={8}>
              <Pagination
                style={{
                  width: "fit-content",
                }}
                ellipsisItem={null}
                disabled={isLoading}
                onPageChange={onPageChange}
                activePage={activePage}
                totalPages={totalPages}
              />
            </Grid.Column>
            <Grid.Column width={8} textAlign="right" verticalAlign="middle">
              Showing Items{" "}
              <strong>
                {currentItem} to {lastItem}
              </strong>{" "}
              of <strong>{totalRecords}</strong>.
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </div>
  );
};

export default EventDetailsTab;
